import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Trash2, ArrowLeft, Shield, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { getErrorMessage } from "@/lib/errorHandler";
import { DataEraseDialog } from "@/components/DataEraseDialog";
interface UserAccount {
  id: string;
  email: string;
  created_at: string;
  profiles?: {
    full_name: string;
    approval_status: string;
  };
  roles?: string[];
  instructor_name?: string;
  branch_id?: string | null;
}

interface Branch {
  id: string;
  name: string;
}
const ManageAccounts = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [accounts, setAccounts] = useState<UserAccount[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<UserAccount | null>(null);
  const [pinInput, setPinInput] = useState("");
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserAccount | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedBranch, setSelectedBranch] = useState<string>("");
  const [deleting, setDeleting] = useState(false);
  const [dataEraseDialogOpen, setDataEraseDialogOpen] = useState(false);
  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }
      const {
        data: profile
      } = await supabase.from("profiles").select("full_name").eq("id", session.user.id).single();
      if (profile) {
        setUserName(profile.full_name);
      }

      // Check if user is admin, manager, or super_admin
      const {
        data: roles
      } = await supabase.from("user_roles").select("role").eq("user_id", session.user.id);
      const hasAccess = roles?.some((r: any) => r.role === "admin" || r.role === "manager" || r.role === "super_admin");
      setIsAdmin(Boolean(hasAccess));
      if (!hasAccess) {
        toast.error("Access denied - Admin, Manager, or Super Admin only");
        navigate("/dashboard");
        return;
      }
      loadAccounts();
      loadBranches();
    };
    checkAuth();
  }, [navigate]);

  const loadBranches = async () => {
    const { data } = await supabase.from("batches").select("id, name").order("name");
    setBranches(data || []);
  };

  const loadAccounts = async () => {
    try {
      setLoading(true);

      // Fetch all profiles
      const {
        data: profiles,
        error: profilesError
      } = await supabase.from("profiles").select("id, email, full_name, created_at, approval_status");
      if (profilesError) throw profilesError;

      // Fetch instructors to get created_at dates and names for users who are instructors
      const {
        data: instructors,
        error: instructorsError
      } = await supabase.from("instructors").select("user_id, created_at, name");
      if (instructorsError) throw instructorsError;

      // Fetch user roles
      const {
        data: userRoles,
        error: rolesError
      } = await supabase.from("user_roles").select("user_id, role, branch_id");
      if (rolesError) throw rolesError;

      // Combine data
      const accountsData: UserAccount[] = profiles.map(profile => {
        const instructor = instructors?.find(i => i.user_id === profile.id);
        const userRoleData = userRoles?.filter(r => r.user_id === profile.id) || [];
        const roles = userRoleData.map(r => r.role);
        const branchId = userRoleData.find(r => r.branch_id)?.branch_id || null;
        return {
          id: profile.id,
          email: profile.email,
          created_at: instructor?.created_at || profile.created_at,
          profiles: {
            full_name: profile.full_name,
            approval_status: profile.approval_status
          },
          roles,
          instructor_name: instructor?.name,
          branch_id: branchId
        };
      });
      setAccounts(accountsData);
    } catch (error: any) {
      toast.error("Failed to load accounts: " + error.message);
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteClick = (account: UserAccount) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };
  const handleRoleClick = (account: UserAccount) => {
    setSelectedUser(account);
    setSelectedRole(account.roles?.[0] || "");
    setSelectedBranch(account.branch_id || "");
    setRoleDialogOpen(true);
  };
  const handleApprovalStatusChange = async (userId: string, status: 'approved' | 'rejected') => {
    try {
      const {
        error
      } = await supabase.from("profiles").update({
        approval_status: status
      }).eq("id", userId);
      if (error) throw error;
      toast.success(`User ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      loadAccounts();
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    }
  };
  const handleRoleUpdate = async () => {
    if (!selectedUser || !selectedRole) {
      toast.error("Please select a role");
      return;
    }

    // Require branch for managers
    if (selectedRole === "manager" && (!selectedBranch || selectedBranch === "none")) {
      toast.error("Please select a branch for the manager");
      return;
    }

    try {
      // Remove existing roles for this user
      await supabase.from("user_roles").delete().eq("user_id", selectedUser.id);

      // Add new role with branch_id if applicable
      const roleData: any = {
        user_id: selectedUser.id,
        role: selectedRole
      };

      // Add branch_id for managers, admins, and instructors (not "none")
      if ((selectedRole === "manager" || selectedRole === "admin" || selectedRole === "instructor") && selectedBranch && selectedBranch !== "none") {
        roleData.branch_id = selectedBranch;
      }

      const {
        error
      } = await supabase.from("user_roles").insert(roleData);
      if (error) throw error;

      // Also update instructor record if user is instructor
      if (selectedRole === "instructor") {
        const branchToSet = selectedBranch && selectedBranch !== "none" ? selectedBranch : null;
        await supabase
          .from("instructors")
          .update({ branch_id: branchToSet })
          .eq("user_id", selectedUser.id);
      }

      toast.success(selectedRole === 'instructor' ? "Instructor role and branch updated." : "Role updated successfully");
      setRoleDialogOpen(false);
      setSelectedBranch("");
      loadAccounts();
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    }
  };
  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;
    try {
      // Verify PIN using server-side function
      const {
        data: isVerified,
        error: verifyError
      } = await supabase.rpc('verify_admin_action', {
        _pin: pinInput,
        _action: 'Delete Account',
        _details: {
          account_id: accountToDelete.id,
          email: accountToDelete.email,
          name: accountToDelete.profiles?.full_name
        }
      });
      if (verifyError || !isVerified) {
        toast.error(getErrorMessage(verifyError));
        setPinInput("");
        return;
      }
      setDeleting(true);
      const {
        data: result,
        error: invokeError
      } = await supabase.functions.invoke('delete-user-account', {
        body: {
          userId: accountToDelete.id
        }
      });
      if (invokeError) {
        throw new Error(invokeError.message || 'Failed to delete account');
      }
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to delete account');
      }
      toast.success("Account deleted successfully");
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
      setPinInput("");
      loadAccounts();
    } catch (error: any) {
      console.error("Delete account error:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };
  const filteredAccounts = accounts.filter(account => `${account.profiles?.full_name} ${account.email}`.toLowerCase().includes(searchTerm.toLowerCase()));
  return <Layout userName={userName}>
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl sm:text-2xl">Manage User Accounts</CardTitle>
{isAdmin && <Button variant="destructive" size="sm" onClick={() => setDataEraseDialogOpen(true)}>
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Erase All Data
                </Button>}
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-6">
              <Input placeholder="Search by name or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>

            {loading ? <p className="text-center text-muted-foreground py-8">Loading accounts...</p> : filteredAccounts.length === 0 ? <p className="text-center text-muted-foreground py-8">
                {searchTerm ? "No accounts found matching your search." : "No accounts found."}
              </p> : <div className="space-y-3">
                {filteredAccounts.map(account => {
                  const branchName = account.branch_id ? branches.find(b => b.id === account.branch_id)?.name : null;
                  return (
                    <div key={account.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base sm:text-lg truncate">
                          {account.instructor_name || account.profiles?.full_name || "No Name"}
                        </h3>
                        <p className="text-sm text-muted-foreground truncate">{account.email}</p>
                        {account.instructor_name && <p className="text-xs text-muted-foreground">Instructor Account</p>}
                        <p className="text-xs text-muted-foreground">
                          Created: {new Date(account.created_at).toLocaleDateString()}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          <Badge variant={account.profiles?.approval_status === 'approved' ? 'default' : account.profiles?.approval_status === 'pending' ? 'secondary' : 'destructive'} className="text-xs">
                            {account.profiles?.approval_status || 'pending'}
                          </Badge>
                          {account.roles?.map(role => <Badge key={role} variant="secondary" className="text-xs">
                              {role}
                            </Badge>) || <Badge variant="outline" className="text-xs">No Role</Badge>}
                          {branchName && (
                            <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/20">
                              {branchName}
                            </Badge>
                          )}
                          {(account.roles?.includes('manager') || account.roles?.includes('admin')) && !branchName && (
                            <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                              No Branch
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        {account.profiles?.approval_status === 'pending' && <>
                            <Button variant="default" size="sm" onClick={() => handleApprovalStatusChange(account.id, 'approved')} className="flex-1 sm:flex-none">
                              <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleApprovalStatusChange(account.id, 'rejected')} className="flex-1 sm:flex-none">
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </>}
                      <Button variant="outline" size="sm" onClick={() => handleRoleClick(account)} className="flex-1 sm:flex-none">
                        <Shield className="w-4 h-4 mr-2" />
                        Manage Role
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteClick(account)} className="flex-1 sm:flex-none">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>}
          </CardContent>
        </Card>

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete User Account</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                You are about to delete the account for{" "}
                <strong>{accountToDelete?.profiles?.full_name}</strong> ({accountToDelete?.email}).
                This action cannot be undone.
              </p>
              <div className="space-y-2">
                <Label htmlFor="deletePin">Enter PIN to confirm</Label>
                <Input id="deletePin" type="password" placeholder="Enter PIN" value={pinInput} onChange={e => setPinInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleDeleteConfirm()} maxLength={6} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
              setDeleteDialogOpen(false);
              setPinInput("");
              setAccountToDelete(null);
            }}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete Account"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Manage User Role</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  User: {selectedUser?.profiles?.full_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  Email: {selectedUser?.email}
                </p>
              </div>
              <div>
                <Label htmlFor="role">Select Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin - Full Access</SelectItem>
                    <SelectItem value="manager">Manager - Admin Privileges</SelectItem>
                    <SelectItem value="instructor">Instructor - Limited Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Branch selection for all roles */}
              <div>
                <Label htmlFor="branch">Assign Branch {selectedRole === "manager" ? "*" : "(optional)"}</Label>
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a branch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Branch</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedRole === "manager" 
                    ? "Managers must be assigned to a branch to view students" 
                    : selectedRole === "instructor"
                    ? "Assign instructor to a specific branch"
                    : "Admins can be assigned to a specific branch"}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleRoleUpdate}>
                Update Role
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DataEraseDialog open={dataEraseDialogOpen} onOpenChange={setDataEraseDialogOpen} />
      </div>
    </Layout>;
};
export default ManageAccounts;